import { useEffect, useRef, useState } from "react";
import { SearchInput } from "@/components/molecules/SearchInput";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { FilterBar, FilterBarLabel } from "@/components/molecules/FilterBar";
import { Tooltip } from "@/components/atoms/Tooltip";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { PropertyFilter } from "@/types/entities";

interface Props {
  filter: PropertyFilter;
  onChange: (next: Partial<PropertyFilter>) => void;
  /** Resets every filter (not the sort order). */
  onClear: () => void;
}

const TYPE_OPTIONS = [
  { value: "", label: "Any type" },
  { value: "Rent", label: "Rent" },
  { value: "SALES", label: "Sales" },
];

export function PropertiesFilters({ filter, onChange, onClear }: Props) {
  const [search, setSearch] = useState(filter.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  useEffect(() => {
    if ((filter.search ?? "") === debouncedSearch) return;
    onChange({ search: debouncedSearch || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    const next = filter.search ?? "";
    setSearch((prev) => {
      if (prev === next) return prev;
      if (prev !== debouncedSearchRef.current) return prev;
      return next;
    });
  }, [filter.search]);

  return (
    <FilterBar
      onClear={() => {
        // The price/bedroom inputs read straight from `filter`, so they empty
        // themselves; the search box holds its own state and has to be told.
        setSearch("");
        onClear();
      }}
    >
      <FilterBarLabel />
      <ScopePicker page="properties" />
      <Tooltip side="top" hoverOnly content="Search listings by address or postcode.">
        <div className="w-full max-w-[620px]">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by address, postcode..." />
        </div>
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Show only sales or only rental listings.">
        <div className="min-w-[170px] max-w-[200px]">
          <MenuSelect
            hideLabel
            value={filter.market ?? ""}
            onChange={(v) => onChange({ market: v || undefined })}
            options={TYPE_OPTIONS}
            placeholder="Any type"
          />
        </div>
      </Tooltip>
      {/* Price, bedrooms, sync status and availability now filter from their
          own column headers in the table below. */}
    </FilterBar>
  );
}